import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { Button } from "./Button";

/** Admin panel to manage member groups (teams) — create, add/remove members. */
export function TeamsAdmin() {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const q = useQuery({ queryKey: queryKeys.teams(), queryFn: api.teams });
  const roster = useQuery({
    queryKey: queryKeys.team(),
    queryFn: api.listTeam,
  });
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: queryKeys.teams() });

  const create = useMutation({
    mutationFn: () => api.createTeam(name.trim()),
    onSuccess: () => {
      setName("");
      invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteTeam(id),
    onSuccess: invalidate,
  });
  const addMember = useMutation({
    mutationFn: (v: { teamId: string; userId: string }) =>
      api.addTeamMember(v.teamId, v.userId),
    onSuccess: invalidate,
  });
  const removeMember = useMutation({
    mutationFn: (v: { teamId: string; userId: string }) =>
      api.removeTeamMember(v.teamId, v.userId),
    onSuccess: invalidate,
  });

  const teams = q.data?.teams ?? [];
  const allMembers = roster.data?.members ?? [];

  return (
    <div className="anc-admin">
      <h3 className="anc-admin-h">Teams</h3>
      <form
        className="anc-new"
        style={{ display: "flex", gap: 6, maxWidth: 420 }}
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) create.mutate();
        }}
      >
        <input
          className="input"
          placeholder="New team name…"
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label="New team name"
        />
        <Button
          variant="primary"
          type="submit"
          disabled={!name.trim() || create.isPending}
        >
          Create
        </Button>
      </form>

      <ul className="tm-list">
        {teams.map((t) => {
          const memberIds = new Set(t.members.map((m) => m.userId));
          const addable = allMembers.filter((u) => !memberIds.has(u.id));
          return (
            <li key={t.id} className="tm-card">
              <div className="tm-head">
                <span className="tm-name">{t.name}</span>
                <span className="t-sm t-muted">
                  {t.members.length} member{t.members.length === 1 ? "" : "s"}
                </span>
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => remove.mutate(t.id)}
                  aria-label={`Delete team ${t.name}`}
                >
                  Delete
                </button>
              </div>
              <div className="tm-members">
                {t.members.map((m) => (
                  <span key={m.userId} className="tm-chip">
                    {m.name}
                    <button
                      type="button"
                      aria-label={`Remove ${m.name} from ${t.name}`}
                      onClick={() =>
                        removeMember.mutate({ teamId: t.id, userId: m.userId })
                      }
                    >
                      ✕
                    </button>
                  </span>
                ))}
                {addable.length > 0 && (
                  <select
                    className="select"
                    value=""
                    style={{ width: 160 }}
                    aria-label={`Add member to ${t.name}`}
                    onChange={(e) => {
                      if (e.target.value)
                        addMember.mutate({
                          teamId: t.id,
                          userId: e.target.value,
                        });
                    }}
                  >
                    <option value="">+ Add member…</option>
                    {addable.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name ?? u.email}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </li>
          );
        })}
        {teams.length === 0 && <li className="t-sm t-muted">No teams yet.</li>}
      </ul>
    </div>
  );
}
